import React, { useState, useEffect } from 'react';
import './App.css';
import EmployeeForm from './EmployeeForm';
import EmployeeModal from './EmployeeModal';
import Login from './Login';
import Tabs from './Tabs';
import VacationStats from './VacationStats';
import VacationForm from './VacationForm';
import VacationList from './VacationList';
import ShiftToggle from './ShiftToggle';
import DismissForm from './DismissForm';
import DismissedTab from './DismissedTab';
import SlotForm from './SlotForm';
import AttendanceForm from './AttendanceForm';
import AttendanceList from './AttendanceList';
import UsersManager from './UsersManager';
import AssignSlotModal from './AssignSlotModal';
import TodayStatsCard from './TodayStatsCard';
import TodayStatsModal from './TodayStatsModal';
import ProductionPlanCard from './ProductionPlanCard';
import ProductionPlanModal from './ProductionPlanModal';
import { LeavingSoonCard, LeavingSoonList } from './LeavingSoon';

// Локальная дата YYYY-MM-DD
const getLocalDate = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Универсальный хелпер: всегда массив
const ensureArray = (x) => (Array.isArray(x) ? x : []);

function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  const [tree, setTree] = useState([]);
  const [hub, setHub] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [zones, setZones] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('main');

  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [vacationEmployee, setVacationEmployee] = useState(null);
  const [vacationListType, setVacationListType] = useState(null);
  const [dismissEmployee, setDismissEmployee] = useState(null);
  const [showLeavingSoon, setShowLeavingSoon] = useState(false);
  const [addingSlotFor, setAddingSlotFor] = useState(null);
  const [attendanceZone, setAttendanceZone] = useState(null);
  const [showAttendanceList, setShowAttendanceList] = useState(null);
  const [showUsersManager, setShowUsersManager] = useState(false);
  const [assigningSlot, setAssigningSlot] = useState(null);
  const [showTodayStats, setShowTodayStats] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set());

  const [productionPlan, setProductionPlan] = useState(null);
  const [showProductionModal, setShowProductionModal] = useState(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterDayOff, setFilterDayOff] = useState('');

  const [activeTab, setActiveTab] = useState(null);
  const [activeWorkshop, setActiveWorkshop] = useState(null);
  const [activeParentZone, setActiveParentZone] = useState(null);
  const [shift, setShift] = useState(() => localStorage.getItem('shift') || 'day');

  useEffect(() => {
    localStorage.setItem('shift', shift);
    document.body.className = shift === 'night' ? 'theme-night' : 'theme-day';
  }, [shift]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {}
    }
    setAuthChecked(true);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user) fetchData();
  }, [user, shift]);

  // Загрузка плана на сегодня
  useEffect(() => {
    if (!user) return;

    fetch(`/api/production?date=${getLocalDate()}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
      .then((r) => r.json())
      .then((data) => setProductionPlan(data))
      .catch(() => setProductionPlan(null));
  }, [user, showProductionModal]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [t, h, d, w, z, r] = await Promise.all([
        fetch(`/api/tree?shift=${shift}`, { headers }),
        fetch(`/api/hub?shift=${shift}`, { headers }),
        fetch(`/api/dismissed?shift=${shift}`, { headers }),
        fetch('/api/workshops', { headers }),
        fetch('/api/zones', { headers }),
        fetch('/api/roles', { headers })
      ]);

      setTree(ensureArray(await t.json()));
      setHub(ensureArray(await h.json()));
      setDismissed(ensureArray(await d.json()));
      setWorkshops(ensureArray(await w.json()));
      setZones(ensureArray(await z.json()));
      setRoles(ensureArray(await r.json()));
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    window.location.reload();
  };

  const getInitials = (fullName) => {
    if (!fullName) return '?';
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const canSee = (emp) => {
    if (!user) return false;
    if (user.role === 'director' || user.role === 'hr' || user.role === 'admin') return true;
    if (user.role === 'workshop_manager') return emp.workshop_id === user.workshop_id;
    return false;
  };

  const canEdit = (emp) => {
    if (!user) return false;
    if (user.role === 'director' || user.role === 'hr' || user.role === 'admin') return true;
    if (user.role === 'workshop_manager') return emp.workshop_id === user.workshop_id;
    return false;
  };

  const canEditZone = (zoneId) => {
    if (!user) return false;
    if (user.role === 'director' || user.role === 'hr' || user.role === 'admin') return true;
    if (user.role === 'workshop_manager') {
      const zone = zones.find((z) => z.id === zoneId);
      return zone && zone.workshop_id === user.workshop_id;
    }
    return false;
  };

  const canManageUsers = () => {
    if (!user) return false;
    return user.role === 'director' || user.role === 'admin';
  };

  const canEditPlan = () => {
    if (!user) return false;
    return user.role === 'director' || user.role === 'admin';
  };

  const isOnVacation = (emp) => {
    if (!emp.vacation_start || !emp.vacation_end) return false;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const start = new Date(emp.vacation_start);
    const end = new Date(emp.vacation_end);
    return start <= today && end >= today;
  };

  const isGoingSoon = (emp) => {
    if (!emp.vacation_start) return false;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const in30 = new Date(today); in30.setDate(in30.getDate() + 30);
    const start = new Date(emp.vacation_start);
    return start > today && start <= in30;
  };

  const isComingBackSoon = (emp) => {
    if (!emp.vacation_end) return false;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const in30 = new Date(today); in30.setDate(in30.getDate() + 30);
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    const end = new Date(emp.vacation_end);
    return end >= yesterday && end <= in30;
  };

  const isLeavingSoon = (emp) => {
    if (!emp.dismissal_date) return false;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const in30 = new Date(today); in30.setDate(in30.getDate() + 30);
    const date = new Date(emp.dismissal_date);
    return date >= today && date <= in30;
  };

  const matchesFilters = (emp) => {
    if (!emp) return false;
    if (!canSee(emp)) return false;
    if (debouncedSearch) {
      const s = debouncedSearch.toLowerCase();
      const ok =
        (emp.full_name || '').toLowerCase().includes(s) ||
        (emp.tab_number || '').toLowerCase().includes(s) ||
        (emp.phone || '').toLowerCase().includes(s);
      if (!ok) return false;
    }
    if (filterType === 'vakhta' && !emp.is_vakhta) return false;
    if (filterType === 'linear' && emp.is_vakhta) return false;
    if (filterDayOff && emp.day_off !== filterDayOff) return false;
    return true;
  };

  const handleRowClick = (emp) => setViewingEmployee(emp);

  const handleEditFromModal = (emp) => {
    setViewingEmployee(null);
    setEditingEmployee(emp);
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditingEmployee(null);
    setShowForm(true);
  };

  const handleSave = async (data) => {
    try {
      const token = localStorage.getItem('token');
      const url = editingEmployee
        ? `/api/employees/${editingEmployee.id}`
        : '/api/employees';
      const method = editingEmployee ? 'PUT' : 'POST';

      const body = { ...data, shift: data.shift || shift };

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (!res.ok) throw new Error('Ошибка сохранения');
      setShowForm(false);
      setEditingEmployee(null);
      fetchData();
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const handleVacation = (emp) => setVacationEmployee(emp);

  const handleVacationSave = async ({ start, end }) => {
    if (!vacationEmployee) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(
        `/api/employees/${vacationEmployee.id}/vacation`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ start, end })
        }
      );
      setVacationEmployee(null);
      setViewingEmployee(null);
      fetchData();
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const handleDismiss = (emp) => setDismissEmployee(emp);

  const handleDismissSave = async ({ dismissal_date }) => {
    if (!dismissEmployee) return;
    try {
      const token = localStorage.getItem('token');

      if (dismissal_date === null) {
        await fetch(`/api/employees/${dismissEmployee.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ ...dismissEmployee, dismissal_date: null })
        });
      } else {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const date = new Date(dismissal_date);

        if (date <= today) {
          await fetch(`/api/employees/${dismissEmployee.id}/dismiss`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ dismissal_date })
          });
        } else {
          await fetch(`/api/employees/${dismissEmployee.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ ...dismissEmployee, dismissal_date })
          });
        }
      }

      setDismissEmployee(null);
      setViewingEmployee(null);
      fetchData();
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const handleRestore = async (emp) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `/api/employees/${emp.id}/restore`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!res.ok) throw new Error('Ошибка возврата');
      fetchData();
      setViewingEmployee(null);
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  // ===== РАБОТА СО СЛОТАМИ =====
  const handleAddSlot = (section, zoneId) => {
    if (!canEditZone(zoneId)) {
      alert('У вас нет прав на изменение этой зоны');
      return;
    }
    setAddingSlotFor({ section, zoneId });
  };

  const handleSaveSlot = async (data) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/slots', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Ошибка добавления');

      setAddingSlotFor(null);
      fetchData();
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const handleDeleteSlot = async (slot, zoneId) => {
    if (!canEditZone(zoneId)) {
      alert('У вас нет прав');
      return;
    }

    let confirmText = `Удалить место "${slot.role_name}"?`;
    if (slot.employee) {
      confirmText = `Сотрудник "${slot.employee.full_name}" будет возвращён в хаб цеха.\n\nУдалить место?`;
    }

    if (!window.confirm(confirmText)) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/slots/${slot.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Ошибка удаления');
      fetchData();
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  // ===== ПОСАДИТЬ НА ВАКАНСИЮ =====
  const handleAssignClick = (slot, zoneId, workshopId) => {
    if (!canEditZone(zoneId)) {
      alert('У вас нет прав');
      return;
    }
    if (slot.employee) {
      alert('Это место занято');
      return;
    }
    setAssigningSlot({ slot, zoneId, workshopId });
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const handleAssignConfirm = async (emp) => {
    if (!assigningSlot) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `/api/slots/${assigningSlot.slot.id}/assign`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ employee_id: emp.id })
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Ошибка');

      setAssigningSlot(null);
      fetchData();
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  // ===== ОТЧЁТЫ =====
  const handleOpenAttendance = (zoneId, zoneName) => {
    if (!canEditZone(zoneId)) {
      alert('У вас нет прав на заполнение отчёта в этой зоне');
      return;
    }
    const today = getLocalDate();
    setAttendanceZone({ zoneId, zoneName, date: today, shift });
  };

  const handleOpenAttendanceDate = (zoneId, zoneName, date, shiftOfReport) => {
    const dateOnly = date.toString().split('T')[0];
    setAttendanceZone({ zoneId, zoneName, date: dateOnly, shift: shiftOfReport });
  };

  const handleShowAttendanceList = (zoneId, zoneName) => {
    setShowAttendanceList({ zoneId, zoneName });
  };

  // ===== ТАБЛИЦА ХАБА =====
  const EmployeeTable = ({ employees }) => {
    const list = ensureArray(employees).filter(matchesFilters);
    if (list.length === 0) {
      return (
        <div className="empty">
          <div style={{ fontSize: '24px', marginBottom: '6px', opacity: 0.5 }}>📭</div>
          Нет сотрудников
        </div>
      );
    }
    return (
      <table className="table">
        <thead>
          <tr>
            <th className="col-tab">Табель</th>
            <th className="col-name">ФИО</th>
            <th className="col-dayoff">Выходной</th>
            <th className="col-status">Статус</th>
          </tr>
        </thead>
        <tbody>
          {list.map((emp) => (
            <tr
              key={emp.id}
              onClick={() => handleRowClick(emp)}
              className={`${canEdit(emp) ? 'editable' : ''} ${
                isLeavingSoon(emp)
                  ? 'leaving-soon-row'
                  : isOnVacation(emp)
                  ? 'on-vacation-row'
                  : isGoingSoon(emp)
                  ? 'going-soon-row'
                  : isComingBackSoon(emp)
                  ? 'coming-back-row'
                  : ''
              }`}
            >
              <td className="col-tab-value">{emp.tab_number || '—'}</td>
              <td className="emp-name">
                <div className="emp-name-cell">
                  <div className={`emp-avatar ${emp.gender === 'м' ? 'male' : 'female'}`}>
                    {getInitials(emp.full_name)}
                  </div>
                  {emp.full_name}
                </div>
              </td>
              <td className="col-dayoff-value">{emp.day_off || '—'}</td>
              <td>
                {isLeavingSoon(emp) && (
                  <span className="badge badge-leaving">
                    ⚠️ Уходит {new Date(emp.dismissal_date).toLocaleDateString('ru-RU')}
                  </span>
                )}
                {isOnVacation(emp) && <span className="badge badge-vacation">🏖 Отпуск</span>}
                {isGoingSoon(emp) && <span className="badge badge-vakhta">📅 Скоро</span>}
                {isComingBackSoon(emp) && <span className="badge badge-vacation">↩️ Возврат</span>}
                {emp.is_vakhta && <span className="badge badge-vakhta">Вахта</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // ===== ТАБЛИЦА УЧАСТКА =====
  const SectionTable = ({ slots, zoneId, workshopId, sectionName, onDeleteSlot, editable, onAssign }) => {
    const hasActiveFilter = debouncedSearch || filterType !== 'all' || filterDayOff;

    const slotsToShow = ensureArray(slots).filter((slot) => {
      if (!slot.employee) return !hasActiveFilter;
      return matchesFilters(slot.employee);
    });

    if (slotsToShow.length === 0) return null;

    return (
      <table className="table">
        <thead>
          <tr>
            <th className="col-tab">Табель</th>
            <th className="col-name">ФИО</th>
            <th className="col-dayoff">Выходной</th>
            <th className="col-role">Должность</th>
            {editable && <th style={{ width: '40px' }}></th>}
          </tr>
        </thead>
        <tbody>
          {slotsToShow.map((slot) => {
            const emp = slot.employee;

            if (emp) {
              return (
                <tr
                  key={slot.id}
                  onClick={() => handleRowClick(emp)}
                  className={`${canEdit(emp) ? 'editable' : ''} ${
                    isLeavingSoon(emp)
                      ? 'leaving-soon-row'
                      : isOnVacation(emp)
                      ? 'on-vacation-row'
                      : isGoingSoon(emp)
                      ? 'going-soon-row'
                      : isComingBackSoon(emp)
                      ? 'coming-back-row'
                      : ''
                  }`}
                >
                  <td className="col-tab-value">{emp.tab_number || '—'}</td>
                  <td className="emp-name">
                    <div className="emp-name-cell">
                      <div className={`emp-avatar ${emp.gender === 'м' ? 'male' : 'female'}`}>
                        {getInitials(emp.full_name)}
                      </div>
                      {emp.full_name}
                    </div>
                  </td>
                  <td className="col-dayoff-value">{emp.day_off || '—'}</td>
                  <td>
                    <span className="role-label">{slot.role_name || '—'}</span>
                    {isLeavingSoon(emp) && <span className="badge badge-leaving">⚠️</span>}
                    {isOnVacation(emp) && <span className="badge badge-vacation">🏖</span>}
                    {isGoingSoon(emp) && <span className="badge badge-vakhta">📅</span>}
                    {isComingBackSoon(emp) && <span className="badge badge-vacation">↩️</span>}
                    {emp.is_vakhta && <span className="badge badge-vakhta">Вахта</span>}
                  </td>
                  {editable && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn-delete-slot"
                        onClick={() => onDeleteSlot(slot)}
                        title="Удалить место"
                      >
                        🗑
                      </button>
                    </td>
                  )}
                </tr>
              );
            }

            return (
              <tr
                key={slot.id}
                className="vacancy-row clickable-vacancy"
                onClick={() =>
                  editable &&
                  onAssign({ ...slot, section_name: sectionName }, zoneId, workshopId)
                }
                title={editable ? 'Нажмите, чтобы назначить человека' : ''}
                style={{ cursor: editable ? 'pointer' : 'default' }}
              >
                <td className="col-tab-value">—</td>
                <td className="emp-name vacancy-text">❌ Вакансия</td>
                <td className="col-dayoff-value">—</td>
                <td><span className="role-label">{slot.role_name || '—'}</span></td>
                {editable && (
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      className="btn-delete-slot"
                      onClick={() => onDeleteSlot(slot)}
                      title="Удалить место"
                    >
                      🗑
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  if (!authChecked) return <div className="loading">⏳ Загрузка...</div>;
  if (!user) return <Login onLogin={setUser} />;
  if (loading) return <div className="loading">⏳ Загрузка данных...</div>;
  if (error) return <div className="error">❌ Ошибка: {error}</div>;

  const getRoleName = (role) => {
    if (role === 'director') return '👑 Директор';
    if (role === 'admin') return '🛡 Админ';
    if (role === 'hr') return '👔 Отдел кадров';
    if (role === 'workshop_manager') return '🏭 Начальник цеха';
    return role;
  };

  // ===== УВОЛЕННЫЕ =====
  if (view === 'dismissed') {
    const visibleDismissed = ensureArray(dismissed).filter(canSee);

    return (
      <>
        <DismissedTab
          employees={visibleDismissed}
          onBack={() => setView('main')}
          onEmployeeClick={(emp) => setViewingEmployee(emp)}
          onRestore={handleRestore}
        />

        {viewingEmployee && (
          <EmployeeModal
            employee={viewingEmployee}
            onClose={() => setViewingEmployee(null)}
            onEdit={() => {
              alert('Чтобы редактировать, сначала верните сотрудника в общий список');
            }}
            onVacation={() => {}}
            onDismiss={() => {}}
            canEdit={false}
          />
        )}
      </>
    );
  }

  // ===== ГЛАВНЫЙ ЭКРАН =====
  const workshopTabs = [];

  if (user.role === 'director' || user.role === 'hr' || user.role === 'admin') {
    workshopTabs.push({
      id: 'hub',
      icon: '📥',
      label: 'На распределение',
      count: ensureArray(hub).filter(matchesFilters).length
    });
  }

  ensureArray(tree).forEach((w) => {
    if (user.role === 'workshop_manager' && user.workshop_id !== w.id) return;

    let total = 0;
    let occupied = 0;
    ensureArray(w.zones).forEach((z) => {
      ensureArray(z.sections).forEach((s) => {
        ensureArray(s.slots).forEach((sl) => {
          total++;
          if (sl.employee) occupied++;
        });
      });
    });

    workshopTabs.push({
      id: `workshop-${w.id}`,
      icon: '🏭',
      label: w.name,
      count: `${occupied}/${total}`
    });
  });

  const currentWorkshop =
    activeWorkshop || (workshopTabs.length > 0 ? workshopTabs[0].id : null);

  // ============================================
  // РЯД 1: Зоны-родители (только те, у которых ЕСТЬ подзоны)
  // ============================================
  const parentZoneTabs = [];

  // ============================================
  // РЯД 2: Подзоны активного родителя ИЛИ обычные зоны
  // ============================================
  const zoneTabs = [];

  if (currentWorkshop === 'hub') {
    // На «На распределение» нет зон — пусто
  } else if (currentWorkshop && currentWorkshop.startsWith('workshop-')) {
    const workshopId = parseInt(currentWorkshop.replace('workshop-', ''));
    const w = ensureArray(tree).find((x) => x.id === workshopId);

    if (w) {
      // Проверяем — есть ли в цехе зоны с подзонами
      const hasSubzones = ensureArray(w.zones).some(
        (z) => ensureArray(z.subzones).length > 0
      );

      if (hasSubzones) {
        // ===== ЦЕХ С ПОДЗОНАМИ (Производство) =====

        // 👥 «Без должности» — первой кнопкой в ряду 2
        const prodHubCount = ensureArray(w.hub).filter(
          (e) => canSee(e) && matchesFilters(e)
        ).length;
        parentZoneTabs.push({
          id: `no-slot-${w.id}`,
          icon: '👥',
          label: 'Без должности',
          count: prodHubCount
        });

        // Собираем зоны-родители (у которых есть подзоны)
        ensureArray(w.zones).forEach((z) => {
          if (ensureArray(z.subzones).length === 0) return;

          let total = 0;
          let occupied = 0;
          ensureArray(z.subzones).forEach((sub) => {
            ensureArray(sub.sections).forEach((s) => {
              ensureArray(s.slots).forEach((sl) => {
                total++;
                if (sl.employee && canSee(sl.employee)) occupied++;
              });
            });
          });

          parentZoneTabs.push({
            id: `parent-${z.id}`,
            icon: '📍',
            label: z.name,
            count: `${occupied}/${total}`
          });
        });

        // Определяем активного родителя (первый по умолчанию)
        const currentParentId =
          activeParentZone ||
          (parentZoneTabs.length > 0 ? parentZoneTabs[0].id : null);

        if (currentParentId && currentParentId.startsWith('parent-')) {
          const parentZoneId = parseInt(currentParentId.replace('parent-', ''));
          const parentZone = ensureArray(w.zones).find((z) => z.id === parentZoneId);

          if (parentZone && ensureArray(parentZone.subzones).length > 0) {
            // Показываем подзоны как вкладки
            ensureArray(parentZone.subzones).forEach((sub) => {
              let total = 0;
              let occupied = 0;
              ensureArray(sub.sections).forEach((s) => {
                ensureArray(s.slots).forEach((sl) => {
                  total++;
                  if (sl.employee && canSee(sl.employee)) occupied++;
                });
              });

              // Скрываем подзону, если в текущей смене у неё нет слотов
              if (total === 0) return;

              zoneTabs.push({
                id: `zone-${sub.id}`,
                icon: '📍',
                label: sub.name,
                count: `${occupied}/${total}`
              });
            });
          }
        }

        // Отпуска — всегда в конце
        zoneTabs.push({
          id: 'vacations',
          icon: '📊',
          label: 'Отпуска',
          count: undefined
        });
      } else {
        // ===== ЦЕХ БЕЗ ПОДЗОН (Фасовка, Экспедиция) =====
        // Без должности
        const peopleWithoutSlot = ensureArray(w.hub).filter(
          (e) => canSee(e) && matchesFilters(e)
        );
        zoneTabs.push({
          id: `no-slot-${w.id}`,
          icon: '👥',
          label: 'Без должности',
          count: peopleWithoutSlot.length
        });

        // Все зоны
        ensureArray(w.zones).forEach((z) => {
          let total = 0;
          let occupied = 0;
          ensureArray(z.sections).forEach((s) => {
            ensureArray(s.slots).forEach((sl) => {
              total++;
              if (sl.employee && canSee(sl.employee)) occupied++;
            });
          });

          zoneTabs.push({
            id: `zone-${z.id}`,
            icon: '📍',
            label: z.name,
            count: `${occupied}/${total}`
          });
        });

        // Отпуска
        zoneTabs.push({
          id: 'vacations',
          icon: '📊',
          label: 'Отпуска',
          count: undefined
        });
      }
    }
  }

  const currentTab =
    activeTab && zoneTabs.some((t) => t.id === activeTab)
      ? activeTab
      : activeTab && activeTab.startsWith('no-slot-')
      ? activeTab
      : zoneTabs.length > 0
      ? zoneTabs[0].id
      : null;

  const getEmployeesForCurrentTab = () => {
    const result = [];

    if (currentTab === 'hub') {
      ensureArray(hub).forEach((e) => { if (canSee(e)) result.push(e); });
    } else if (currentTab && currentTab.startsWith('no-slot-')) {
      const workshopId = parseInt(currentTab.replace('no-slot-', ''));
      const w = ensureArray(tree).find((x) => x.id === workshopId);
      if (w) ensureArray(w.hub).forEach((e) => { if (canSee(e)) result.push(e); });
    } else if (currentTab && currentTab.startsWith('zone-')) {
      const zoneId = parseInt(currentTab.replace('zone-', ''));
      ensureArray(tree).forEach((w) => {
        ensureArray(w.zones).forEach((z) => {
          if (z.id !== zoneId) return;
          ensureArray(w.hub).forEach((e) => {
            if (e.zone_id === z.id && canSee(e)) result.push(e);
          });
          ensureArray(z.sections).forEach((s) => {
            ensureArray(s.slots).forEach((sl) => {
              if (sl.employee && canSee(sl.employee)) result.push(sl.employee);
            });
          });
        });
      });
    } else if (currentTab === 'vacations') {
      if (currentWorkshop === 'hub') {
        ensureArray(hub).forEach((e) => { if (canSee(e)) result.push(e); });
      } else if (currentWorkshop && currentWorkshop.startsWith('workshop-')) {
        const workshopId = parseInt(currentWorkshop.replace('workshop-', ''));
        const w = ensureArray(tree).find((x) => x.id === workshopId);
        if (w) {
          ensureArray(w.hub).forEach((e) => { if (canSee(e)) result.push(e); });
          ensureArray(w.zones).forEach((z) => {
            ensureArray(z.sections).forEach((s) => {
              ensureArray(s.slots).forEach((sl) => {
                if (sl.employee && canSee(sl.employee)) result.push(sl.employee);
              });
            });
          });
        }
      }
    }

    const unique = [];
    const ids = new Set();
    result.forEach((e) => {
      if (!ids.has(e.id)) {
        ids.add(e.id);
        unique.push(e);
      }
    });
    return unique;
  };

  const currentTabEmployees = getEmployeesForCurrentTab();

  const getAllActiveEmployees = () => {
    const result = [];
    ensureArray(hub).forEach((e) => { if (canSee(e)) result.push(e); });
    ensureArray(tree).forEach((w) => {
      ensureArray(w.hub).forEach((e) => { if (canSee(e)) result.push(e); });
      ensureArray(w.zones).forEach((z) => {
        ensureArray(z.sections).forEach((s) => {
          ensureArray(s.slots).forEach((sl) => {
            if (sl.employee && canSee(sl.employee)) result.push(sl.employee);
          });
        });
      });
    });
    const unique = [];
    const ids = new Set();
    result.forEach((e) => {
      if (!ids.has(e.id)) {
        ids.add(e.id);
        unique.push(e);
      }
    });
    return unique;
  };

  const renderVacationsTab = () => {
    const currentYear = new Date().getFullYear();
    const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

    const byMonth = months.map((m, i) => {
      const inThisMonth = currentTabEmployees.filter((e) => {
        if (!e.vacation_start || !e.vacation_end) return false;
        const start = new Date(e.vacation_start);
        const end = new Date(e.vacation_end);
        const monthStart = new Date(currentYear, i, 1);
        const monthEnd = new Date(currentYear, i + 1, 0);
        return start <= monthEnd && end >= monthStart;
      });
      return { name: m, employees: inThisMonth };
    });

    const maxCount = Math.max(...byMonth.map((m) => m.employees.length), 1);

    return (
      <div className="vacations-tab">
        <div className="group">
          <div className="group-title">📊 Отпуска по месяцам — {currentYear}</div>
          <div className="months-chart">
            {byMonth.map((m) => (
              <div key={m.name} className="month-row">
                <div className="month-name">{m.name}</div>
                <div className="month-bar-wrap">
                  <div className="month-bar" style={{ width: `${(m.employees.length / maxCount) * 100}%` }}>
                    {m.employees.length > 0 && <span className="month-bar-count">{m.employees.length}</span>}
                  </div>
                </div>
                <div className="month-names">
                  {m.employees.length > 0
                    ? m.employees.slice(0, 3).map((e) => e.full_name.split(' ')[0]).join(', ') +
                      (m.employees.length > 3 ? ` +${m.employees.length - 3}` : '')
                    : '—'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    if (currentWorkshop === 'hub') {
      const visibleHub = ensureArray(hub).filter(matchesFilters);
      return (
        <div className="group">
          <div className="group-title">
            📥 На распределение <span className="count">{visibleHub.length}</span>
          </div>
          <EmployeeTable employees={visibleHub} />
        </div>
      );
    }

    if (!currentTab) return null;

    if (currentTab.startsWith('no-slot-')) {
      const workshopId = parseInt(currentTab.replace('no-slot-', ''));
      const w = ensureArray(tree).find((x) => x.id === workshopId);
      const people = w ? ensureArray(w.hub).filter((e) => canSee(e) && matchesFilters(e)) : [];
      return (
        <div className="group">
          <div className="group-title">
            👥 {w ? w.name : ''} → Без должности <span className="count">{people.length}</span>
          </div>
          <EmployeeTable employees={people} />
        </div>
      );
    }

    if (currentTab === 'vacations') {
      return renderVacationsTab();
    }

    if (currentTab.startsWith('zone-')) {
      const zoneId = parseInt(currentTab.replace('zone-', ''));
      let result = null;
      const editable = canEditZone(zoneId);

      ensureArray(tree).forEach((w) => {
        const allZones = [];
        ensureArray(w.zones).forEach((z) => {
          allZones.push(z);
          ensureArray(z.subzones).forEach((sz) => {
            allZones.push(sz);
          });
        });

        allZones.forEach((z) => {
          if (z.id !== zoneId) return;

          const zoneHub = ensureArray(w.hub).filter((e) => e.zone_id === z.id && canSee(e) && matchesFilters(e));

          result = (
            <React.Fragment key={z.id}>
              {editable && (
                <div className="zone-actions no-print">
                  <button
                    className="btn btn-primary"
                    onClick={() => handleOpenAttendance(z.id, z.name)}
                  >
                    📋 Отчёт за сегодня ({shift === 'day' ? '☀️ День' : '🌙 Ночь'})
                  </button>
                  <button
                    className="btn"
                    onClick={() => handleShowAttendanceList(z.id, z.name)}
                  >
                    📅 Все отчёты
                  </button>
                </div>
              )}

              {zoneHub.length > 0 && (
                <div className="group">
                  <div className="group-title">
                    📥 {z.name} → Люди без должности <span className="count">{zoneHub.length}</span>
                  </div>
                  <EmployeeTable employees={zoneHub} />
                </div>
              )}

              {ensureArray(z.sections).map((section) => {
                const total = ensureArray(section.slots).length;
                const occupied = ensureArray(section.slots).filter((s) => s.employee).length;
                const isExpanded = expandedSections.has(section.id);

                return (
                  <div className="group" key={section.id}>
                    <div
                      className="group-title section-header-clickable"
                      onClick={() => toggleSection(section.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="section-arrow">{isExpanded ? '▼' : '▶'}</span>
                      📍 {section.name}{' '}
                      <span className="count">{occupied}/{total}</span>
                      {editable && (
                        <button
                          className="btn-add-slot"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddSlot(section, z.id);
                          }}
                          title="Добавить рабочее место"
                        >
                          +
                        </button>
                      )}
                    </div>
                    {isExpanded && (
                      <SectionTable
                        slots={section.slots}
                        zoneId={z.id}
                        workshopId={w.id}
                        sectionName={section.name}
                        onDeleteSlot={(slot) => handleDeleteSlot(slot, z.id)}
                        onAssign={handleAssignClick}
                        editable={editable}
                      />
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          );
        });
      });

      return result;
    }

    return null;
  };

  return (
    <div className="app">
      <header className="header no-print">
        <h1>🏭 Расстановка персонала</h1>
        <div className="header-shift">
          <ShiftToggle shift={shift} onChange={setShift} />
        </div>
        <div className="header-production">
          <ProductionPlanCard
            plan={productionPlan}
            date={getLocalDate()}
            canEdit={canEditPlan()}
            onClick={() => setShowProductionModal(true)}
          />
        </div>
        <div className="header-user">
          <span className="user-badge">
            {getRoleName(user.role)} — {user.full_name}
          </span>
          <button className="btn btn-logout" onClick={handleLogout}>🚪 Выйти</button>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={handleAdd}>➕ Добавить</button>
          <button className="btn" onClick={() => setView('dismissed')}>
            📁 Уволенные ({ensureArray(dismissed).filter(canSee).length})
          </button>
          {canManageUsers() && (
            <button className="btn" onClick={() => setShowUsersManager(true)}>
              👥 Пользователи
            </button>
          )}
        </div>
      </header>

      <div className="print-header">
        <h1>КОНТРОЛЬ ШТАТА — {shift === 'day' ? '☀️ ДЕНЬ' : '🌙 НОЧЬ'}</h1>
        <div className="print-meta">
          <span>Дата: {new Date().toLocaleDateString('ru-RU')}</span>
          <span>{getRoleName(user.role)}: {user.full_name}</span>
        </div>
      </div>

      <div className="filters no-print">
        <input
          type="text"
          className="filter-search"
          placeholder="🔍 Поиск по ФИО, табельному, телефону..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="filter-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <option value="all">Все типы</option>
          <option value="vakhta">Только вахта</option>
          <option value="linear">Только линейные</option>
        </select>
        <select className="filter-select" value={filterDayOff} onChange={(e) => setFilterDayOff(e.target.value)}>
          <option value="">Любой выходной</option>
          <option>понедельник</option>
          <option>вторник</option>
          <option>среда</option>
          <option>четверг</option>
          <option>пятница</option>
          <option>суббота</option>
          <option>воскресенье</option>
        </select>
        {(search || filterType !== 'all' || filterDayOff) && (
          <button className="btn-clear" onClick={() => {
            setSearch(''); setFilterType('all'); setFilterDayOff('');
          }}>✖ Сбросить</button>
        )}
      </div>

      <Tabs
        tabs={workshopTabs}
        activeTab={currentWorkshop}
        onChange={(id) => {
          setActiveWorkshop(id);
          setActiveTab(null);
          setActiveParentZone(null);
        }}
      />

      {/* Ряд 2: Зоны-родители + «Без должности» (для цехов с подзонами) */}
      {parentZoneTabs.length > 0 && (
        <Tabs
          tabs={parentZoneTabs}
          activeTab={
            activeTab && activeTab.startsWith('no-slot-')
              ? activeTab
              : activeParentZone ||
                (parentZoneTabs.length > 0 ? parentZoneTabs[0].id : null)
          }
          onChange={(id) => {
            if (id.startsWith('no-slot-')) {
              setActiveTab(id);
            } else {
              setActiveParentZone(id);
              setActiveTab(null);
            }
          }}
        />
      )}


{/* Ряд 3: Подзоны активного родителя — скрываем только для цехов с подзонами */}
{zoneTabs.length > 0 && !(parentZoneTabs.length > 0 && currentTab && currentTab.startsWith('no-slot-')) && (        <Tabs tabs={zoneTabs} activeTab={currentTab} onChange={setActiveTab} />
      )}

      {currentTab !== 'vacations' && (
        <div className="stats-row-3">
          <div className="stats-item">
            <VacationStats
              employees={currentTabEmployees}
              onShowList={(type) => setVacationListType(type)}
            />
          </div>
          <div className="stats-item">
            <TodayStatsCard
              zoneId={
                currentTab && currentTab.startsWith('zone-')
                  ? parseInt(currentTab.replace('zone-', ''))
                  : null
              }
              shift={shift}
              date={getLocalDate()}
              onClick={() => {
                if (currentTab && currentTab.startsWith('zone-')) {
                  const zId = parseInt(currentTab.replace('zone-', ''));
                  const z = ensureArray(zones).find((x) => x.id === zId);
                  setShowTodayStats({
                    zoneId: zId,
                    zoneName: z ? z.name : '',
                    date: getLocalDate(),
                    shift
                  });
                }
              }}
            />
          </div>
          <div className="stats-item">
            <LeavingSoonCard
              employees={currentTabEmployees}
              onShowList={() => setShowLeavingSoon(true)}
            />
          </div>
        </div>
      )}

      <div className="tab-content">{renderTabContent()}</div>

      {viewingEmployee && (
        <EmployeeModal
          employee={viewingEmployee}
          onClose={() => setViewingEmployee(null)}
          onEdit={handleEditFromModal}
          onVacation={handleVacation}
          onDismiss={handleDismiss}
          canEdit={canEdit(viewingEmployee)}
        />
      )}

      {showForm && (
        <EmployeeForm
          employee={editingEmployee}
          workshops={workshops}
          zones={zones}
          roles={roles}
          currentShift={shift}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditingEmployee(null); }}
        />
      )}

      {vacationEmployee && (
        <VacationForm
          employee={vacationEmployee}
          onSave={handleVacationSave}
          onClose={() => setVacationEmployee(null)}
        />
      )}

      {vacationListType && (
        <VacationList
          type={vacationListType}
          employees={currentTabEmployees}
          onClose={() => setVacationListType(null)}
          onEmployeeClick={(emp) => {
            setVacationListType(null);
            setViewingEmployee(emp);
          }}
        />
      )}

      {dismissEmployee && (
        <DismissForm
          employee={dismissEmployee}
          onSave={handleDismissSave}
          onClose={() => setDismissEmployee(null)}
        />
      )}

      {showLeavingSoon && (
        <LeavingSoonList
          employees={currentTabEmployees}
          onClose={() => setShowLeavingSoon(false)}
          onEmployeeClick={(emp) => {
            setShowLeavingSoon(false);
            setViewingEmployee(emp);
          }}
        />
      )}

      {addingSlotFor && (
        <SlotForm
          section={addingSlotFor.section}
          zoneId={addingSlotFor.zoneId}
          onSave={handleSaveSlot}
          onClose={() => setAddingSlotFor(null)}
        />
      )}

      {attendanceZone && (
        <AttendanceForm
          zoneId={attendanceZone.zoneId}
          zoneName={attendanceZone.zoneName}
          date={attendanceZone.date}
          shift={attendanceZone.shift}
          onClose={() => setAttendanceZone(null)}
        />
      )}

      {showAttendanceList && (
        <AttendanceList
          zoneId={showAttendanceList.zoneId}
          zoneName={showAttendanceList.zoneName}
          canDelete={user.role === 'director' || user.role === 'hr' || user.role === 'admin'}
          onClose={() => setShowAttendanceList(null)}
          onViewDate={(date, shiftOfReport) => {
            handleOpenAttendanceDate(
              showAttendanceList.zoneId,
              showAttendanceList.zoneName,
              date,
              shiftOfReport
            );
          }}
        />
      )}

      {showUsersManager && (
        <UsersManager
          currentUser={user}
          workshops={workshops}
          onClose={() => setShowUsersManager(false)}
        />
      )}

      {assigningSlot && (
        <AssignSlotModal
          slot={assigningSlot.slot}
          zoneId={assigningSlot.zoneId}
          workshopId={assigningSlot.workshopId}
          employees={getAllActiveEmployees()}
          onAssign={handleAssignConfirm}
          onClose={() => setAssigningSlot(null)}
        />
      )}

      {showTodayStats && (
        <TodayStatsModal
          zoneId={showTodayStats.zoneId}
          zoneName={showTodayStats.zoneName}
          date={showTodayStats.date}
          shift={showTodayStats.shift}
          onClose={() => setShowTodayStats(null)}
          onEmployeeClick={(emp) => {
            setShowTodayStats(null);
            const fullEmp = currentTabEmployees.find((e) => e.id === emp.employee_id);
            if (fullEmp) setViewingEmployee(fullEmp);
          }}
        />
      )}

      {showProductionModal && (
        <ProductionPlanModal
          date={getLocalDate()}
          onSave={() => {
            setShowProductionModal(false);
          }}
          onClose={() => setShowProductionModal(false)}
        />
      )}
    </div>
  );
}

export default App;
